import { useState } from 'react';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials as mockTestimonials } from '@/mocks/testimonials';

const siteUrl = 'https://xiuxiushipin.com';

const aboutSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于羞羞视频精选合集',
    description: '羞羞视频精选合集是一个专注于趣味搞笑与情感类短视频内容聚合的平台，致力于为用户提供轻松愉快的视频浏览体验。',
    url: `${siteUrl}/about`,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '羞羞视频精选合集每天更新多少视频？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们每天精选更新10-20个热门短视频，涵盖恋爱日常、甜蜜互动、趣味搞笑、搞怪萌宠等多个分类，确保内容新鲜有趣。',
        },
      },
      {
        '@type': 'Question',
        name: '如何推荐好玩的视频给我们？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本页面的联系表单向我们投稿或推荐视频。我们非常欢迎用户分享有趣的视频内容，一经采用会注明来源。',
        },
      },
      {
        '@type': 'Question',
        name: '视频更新频率是怎样的？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们保持每日更新的频率，每天上午10点和下午6点各更新一批精选视频。周末和节假日也会有特别专题推送。',
        },
      },
      {
        '@type': 'Question',
        name: '羞羞视频支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们的网站支持所有主流设备，包括电脑、平板和手机。您可以在任何设备上流畅观看视频，获得最佳的浏览体验。',
        },
      },
    ],
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9th24n8l97n3eas3a10', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEO
        title="关于我们"
        description="羞羞视频精选合集，汇聚全网最热门趣味搞笑与情感类短视频。了解我们的品牌故事、用户口碑，欢迎联系我们投稿或合作。"
        keywords="关于羞羞视频,羞羞视频品牌,联系我们,用户口碑,短视频平台"
        canonicalUrl={`${siteUrl}/about`}
        schemaData={aboutSchema}
      />

      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 bg-background-50">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Hero */}
        <section className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-5">
              关于我们
            </span>
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-900 mb-5">
              让每一天都充满欢乐与温暖
            </h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
              羞羞视频精选合集诞生于一个简单的想法——让每个人在忙碌的生活中，都能找到几分钟的轻松和快乐。
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-5">
                  我们的故事
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    在这个快节奏的时代，我们每个人都面临着各种各样的压力。工作、学习、生活的重担有时会让人喘不过气来。羞羞视频精选合集就是为此而生的。
                  </p>
                  <p>
                    我们的团队每天花费大量时间，从海量的短视频内容中精心筛选出最有趣、最温暖、最治愈的作品，涵盖<strong>恋爱日常</strong>、<strong>甜蜜互动</strong>、<strong>趣味搞笑</strong>、<strong>搞怪萌宠</strong>等多个分类，只为给用户带来最纯粹的笑容。
                  </p>
                  <p>
                    从最初的几十个视频，到如今超过<strong>1000个</strong>精选内容，我们始终坚持"品质优先"的原则。每一个视频都经过团队的精心挑选，确保内容正能量、积极向上，适合所有年龄段的用户观看。
                  </p>
                  <p>
                    我们相信，快乐是可以传递的。当你在这里看到一个让你会心一笑的视频，分享给身边的人，这份快乐就会不断延续下去。
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="aspect-square rounded-3xl overflow-hidden bg-background-100">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20warm%20artistic%20illustration%20of%20people%20watching%20videos%20on%20mobile%20devices%20with%20smiles%2C%20modern%20flat%20illustration%20style%2C%20coral%20and%20peach%20color%20palette%2C%20clean%20minimal%20design%2C%20joyful%20atmosphere%2C%20soft%20gradients%2C%20community%20feeling%2C%20warm%20lighting&width=800&height=800&seq=about-brand&orientation=squarish"
                    alt="羞羞视频品牌形象"
                    className="w-full h-full object-cover"
                    title="羞羞视频 - 品牌形象"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 bg-primary-500 text-background-50 rounded-2xl px-6 py-4 shadow-lg shadow-primary-500/20">
                  <div className="text-2xl font-bold font-heading">1000+</div>
                  <div className="text-xs text-background-100/80">精选视频</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl md:text-4xl font-bold text-primary-500 font-heading mb-2">1000+</div>
                <div className="text-foreground-500 text-sm">精选视频</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold text-accent-500 font-heading mb-2">50万+</div>
                <div className="text-foreground-500 text-sm">日活跃用户</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold text-secondary-500 font-heading mb-2">6</div>
                <div className="text-foreground-500 text-sm">内容分类</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold text-primary-500 font-heading mb-2">99.8%</div>
                <div className="text-foreground-500 text-sm">用户好评率</div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                <i className="ri-star-line mr-1"></i>用户口碑
              </span>
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-3">
                听听他们怎么说
              </h2>
              <p className="text-foreground-500 text-sm">来自真实用户的评价与反馈</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {mockTestimonials.map((t) => (
                <div
                  key={t.id}
                  className="p-6 rounded-2xl bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-100 flex-shrink-0">
                      <img
                        src={t.avatarUrl}
                        alt={t.userName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="font-medium text-foreground-900 text-sm">{t.userName}</div>
                      <div className="text-foreground-400 text-xs">{t.date}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                <i className="ri-question-line mr-1"></i>常见问题
              </span>
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-3">
                你可能想知道
              </h2>
            </div>

            <div className="space-y-3">
              {aboutSchema[1] && 'mainEntity' in aboutSchema[1] && Array.isArray(aboutSchema[1].mainEntity) && aboutSchema[1].mainEntity.map((faq: { name: string; acceptedAnswer: { text: string } }, idx: number) => (
                <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/70 cursor-pointer">
                  <summary className="flex items-center justify-between p-5 font-medium text-foreground-900 text-sm list-none">
                    <span>{faq.name}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-5 text-foreground-600 text-sm leading-relaxed">
                    <p>{faq.acceptedAnswer.text}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-4 md:px-6 py-16 md:py-20">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                <i className="ri-mail-line mr-1"></i>联系我们
              </span>
              <h2 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-3">
                期待你的来信
              </h2>
              <p className="text-foreground-500 text-sm">
                有任何问题、建议或合作意向，欢迎给我们留言
              </p>
            </div>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5 p-6 md:p-8 rounded-2xl bg-background-50 border border-background-200/70"
            >
              {formStatus === 'success' && (
                <div className="p-4 rounded-xl bg-primary-50 text-primary-700 text-sm flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-lg"></i>
                  提交成功！感谢您的留言，我们会尽快回复。
                </div>
              )}

              {formStatus === 'error' && (
                <div className="p-4 rounded-xl bg-red-50 text-red-600 text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line text-lg"></i>
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-2.5 rounded-xl border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="请输入主题"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 rounded-xl border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多可输入500个字符</p>
              </div>

              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'loading' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>提交中...
                  </span>
                ) : (
                  '提交留言'
                )}
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}